import { Modal } from "../Modal";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineCalendar } from "../DateField/components/InlineCalendar";
import { DateField } from "../DateField";
import { SecondaryButton, PrimaryButton } from "../Button";
import type { FormattedTimePreferences } from "../FormattedTime";
import { TimePicker } from "../TimePicker";
import { formatDate } from "../utils/formatting";
import { dateInTimezone, zonedDateTimeToDate } from "../utils/timezone";

export interface ScheduleModalProps {
  children?: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSchedule: (date: Date) => void;
  onCancel: () => void;
  scheduledAt: Date | null;
  formattedTimePreferences: FormattedTimePreferences;
  title: string;
}

export function ScheduleModal({
  children,
  open,
  onOpenChange,
  onSchedule,
  onCancel,
  scheduledAt,
  formattedTimePreferences,
  title,
}: ScheduleModalProps) {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<DateField.ContextualDate | null>(() =>
    contextualDateFromScheduledAt(scheduledAt, formattedTimePreferences),
  );
  const [time, setTime] = useState<Date | null>(() =>
    scheduledAt ? dateInTimezone(scheduledAt, formattedTimePreferences.timezone) : defaultScheduleTime(),
  );
  const now = new Date();
  const today = dateInTimezone(now, formattedTimePreferences.timezone);

  const handleSchedule = () => {
    const finalDate = combineDateAndTime(selectedDate?.date, time, formattedTimePreferences.timezone);

    if (finalDate) {
      onSchedule(finalDate);
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  const scheduledDate = combineDateAndTime(selectedDate?.date, time, formattedTimePreferences.timezone);
  const isNonexistentTime = Boolean(selectedDate && time && !scheduledDate);
  const isInvalid = !scheduledDate || scheduledDate <= now;

  return (
    <>
      {children}
      <Modal isOpen={open} onClose={() => onOpenChange(false)} title={title} size="xx-small">
        <div className="mb-4 mt-2">
          <InlineCalendar
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            minDateLimit={startOfDay(today)}
            today={today}
          />
        </div>

        <div className="mb-6 flex items-center gap-4">
          <label id="schedule-time-label" className="text-sm font-medium text-content-dimmed" htmlFor="schedule-time">
            {t("Time")}
          </label>
          <TimePicker
            value={time}
            onChange={setTime}
            ariaLabelledBy="schedule-time-label"
            id="schedule-time"
            wrapperClassName="ml-auto w-36"
            formattedTimePreferences={formattedTimePreferences}
          />
        </div>

        {isInvalid && selectedDate && (
          <div className="-mb-2 -mt-2 text-right text-xs text-red-500" role="alert">
            {isNonexistentTime ? t("This time does not exist in your timezone") : t("Time must be in the future")}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4 items-center">
          <SecondaryButton onClick={handleCancel} size="sm">
            {t("Cancel")}
          </SecondaryButton>
          <PrimaryButton onClick={handleSchedule} size="sm" disabled={isInvalid} testId="confirm-schedule">
            {t("Schedule")}
          </PrimaryButton>
        </div>
      </Modal>
    </>
  );
}

function startOfDay(date: Date): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
}

function defaultScheduleTime(): Date {
  const time = new Date();
  time.setHours(9, 0, 0, 0);
  return time;
}

function contextualDateFromScheduledAt(
  scheduledAt: Date | null,
  formattedTimePreferences: FormattedTimePreferences,
): DateField.ContextualDate | null {
  if (!scheduledAt) return null;

  const date = dateInTimezone(scheduledAt, formattedTimePreferences.timezone);
  date.setHours(0, 0, 0, 0);

  return {
    date,
    dateType: "day",
    value: formatDate(date, formattedTimePreferences.locale, { year: "numeric", month: "short", day: "numeric" }),
  };
}

function combineDateAndTime(date: Date | undefined, time: Date | null, timezone: string): Date | null {
  if (!date || !time) return null;

  return zonedDateTimeToDate(
    {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: time.getHours(),
      minute: time.getMinutes(),
    },
    timezone,
  );
}
